import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useToasts } from 'react-toast-notifications';

// API
import ParameterApi, { ParameterToSave, ParameterToUpdate, ParameterToDelete } from '../../../api/ParameterApi';

// Types
import Parameter from '../../../types/Parameter';

// Components
import Breadcrumb from '../../../container/breadcrumb';
import HeaderActions from '../../../container/header_actions';
import ParameterList from './ParameterList';

interface ParametersProps {
    isAdmin: boolean;
    loginRequired: boolean;
    toggleMenu: Function;
}

function Parameters(props: ParametersProps) {
    const [loading, setLoading] = useState<boolean>(true);
    const [displayData, setDisplayData] = useState<Parameter[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const { addToast } = useToasts();
    const navigate = useNavigate();

    useEffect(() => {
        if (!props.isAdmin) {
            navigate(`/not-found`)
        }
    }, [props.loginRequired]);

    // To track original data for diffing on save
    const [originalParameters, setOriginalParameters] = useState<Parameter[]>([]);

    // Merge parameters and properties, with parameters taking precedence
    const mergeParametersAndProperties = useCallback((params: Parameter[], props: Parameter[]): Parameter[] => {
        const merged = new Map<string, Parameter>();

        // First add all properties
        props.forEach(prop => {
            merged.set(prop.name, {
                ...prop,
                isProperty: true,
                propertyValue: prop.value
            });
        });

        // Then override with parameters (parameters take precedence)
        params.forEach(param => {
            const existing = merged.get(param.name);
            merged.set(param.name, {
                ...param,
                isProperty: false,
                propertyValue: existing?.propertyValue // Keep property value for reference
            });
        });

        return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [fetchedParameters, fetchedProperties] = await Promise.all([
                ParameterApi.getParameters(),
                ParameterApi.getProperties(),
            ]);

            setOriginalParameters(JSON.parse(JSON.stringify(fetchedParameters))); // Deep copy

            const merged = mergeParametersAndProperties(fetchedParameters, fetchedProperties);
            setDisplayData(merged);

        } catch (error) {
            console.error("Failed to fetch parameters or properties", error);
        } finally {
            setLoading(false);
        }
    }, [mergeParametersAndProperties]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSaveChanges = async () => {
        setLoading(true);
        const parametersToDelete: ParameterToDelete[] = [];
        const parametersToSave: ParameterToSave[] = [];
        const parametersToUpdate: ParameterToUpdate[] = [];

        // Create a map of current parameter values for efficient lookup
        const currentParamMap = new Map<string, Parameter>();
        displayData.forEach(param => {
            if (!param.isProperty) {
                currentParamMap.set(param.name, param);
            }
        });

        // First, identify parameters that need to be deleted
        // A parameter was deleted if it exists in original but not in current (or current is now a property)
        originalParameters.forEach(originalParam => {
            const currentParam = displayData.find(p => p.name === originalParam.name);

            // Parameter should be deleted if it no longer exists as a parameter in current data
            if (!currentParam || currentParam.isProperty) {
                parametersToDelete.push({
                    name: originalParam.name,
                    value: originalParam.value
                });
            }
        });

        // Then, identify parameters that need to be saved (new or updated)
        displayData.forEach(param => {
            // Only process non-property items
            if (!param.isProperty) {
                const originalParam = originalParameters.find(p => p.name === param.name);

                if (originalParam) {
                    // Existing parameter - update if value is different
                    if (originalParam.value !== param.value) {
                        parametersToUpdate.push({
                            name: param.name,
                            value: param.value,
                        });
                    }
                } else {
                    // New parameter - create
                    parametersToSave.push({
                        name: param.name,
                        value: param.value,
                    });
                }
            }
        });

        try {
            // Execute delete operations first
            if (parametersToDelete.length > 0) {
                await ParameterApi.deleteParameter(parametersToDelete);
            }

            // For updates, we need to delete the old value and then post the new value
            // Combine updates with deletes first
            if (parametersToUpdate.length > 0) {
                // First, get the original values for the parameters being updated
                const updateDeletes: ParameterToDelete[] = parametersToUpdate.map(param => {
                    const originalParam = originalParameters.find(p => p.name === param.name);
                    return {
                        name: param.name,
                        value: originalParam?.value || '' // Use original value for delete
                    };
                });

                // Delete the old values
                await ParameterApi.deleteParameter(updateDeletes);

                // Then post the new values (treat updates as new posts)
                parametersToSave.push(...parametersToUpdate);
            }

            // Finally execute save operations for new parameters and updated parameters
            if (parametersToSave.length > 0) {
                for (const param of parametersToSave) {
                    await ParameterApi.saveParameter(param);
                }
            }

            // Refresh data from server to get the latest state
            await fetchData();
            setHasUnsavedChanges(false);
            addToast('Parameters saved successfully', { appearance: 'success', autoDismiss: true });
        } catch (error) {
            console.error('Failed to save changes', error);
            addToast('Failed to save parameters', { appearance: 'error', autoDismiss: true });
        } finally {
            setLoading(false);
        }
    };

    const getUniqueParameterValues = useCallback((paramName: string): string[] => {
        const values = new Set<string>();
        displayData.forEach(param => {
            if (param.name === paramName && param.value) {
                values.add(param.value);
            }
        });
        return Array.from(values).sort();
    }, [displayData]);

    // Callbacks for ParameterList to update state
    const handleCellEdit = (paramName: string, newValue: string | null) => {
        setDisplayData(prevData =>
            prevData.map(param => {
                if (param.name === paramName) {
                    // If editing a property, convert it to a parameter
                    if (param.isProperty) {
                        return {
                            ...param,
                            value: newValue || '',
                            isProperty: false, // Now it's a parameter that overrides the property
                            propertyValue: param.value // Keep the original property value
                        };
                    }
                    return { ...param, value: newValue || '' };
                }
                return param;
            })
        );
        setHasUnsavedChanges(true);
    };

    const handleNewParameter = (newParamName: string, newParamValue: string) => {
        if (newParamName && !displayData.find(param => param.name === newParamName)) {
            const newParam: Parameter = {
                name: newParamName,
                value: newParamValue,
                isProperty: false,
            };
            setDisplayData(prevData => [...prevData, newParam].sort((a, b) => a.name.localeCompare(b.name)));
            setHasUnsavedChanges(true);
        }
    };

    const handleDeleteParameter = (paramName: string) => {
        setDisplayData(prevData => {
            const paramToDelete = prevData.find(p => p.name === paramName);

            // If this parameter has a property value, revert to showing the property
            if (paramToDelete?.propertyValue) {
                return prevData.map(p =>
                    p.name === paramName
                        ? { ...p, value: p.propertyValue || '', isProperty: true, id: undefined }
                        : p
                );
            } else {
                // Otherwise, remove it completely
                return prevData.filter(p => p.name !== paramName);
            }
        });
        setHasUnsavedChanges(true);
    };

    return (
        <div id="content">
            <Helmet>
                <title>Parameters - Settings - DBmarlin</title>
                <meta name="description" content="Manage system parameters and properties" />
            </Helmet>

            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Parameters">
                    <Link to="/admin">Settings</Link>
                    <span>Parameters</span>
                </Breadcrumb>
                <HeaderActions
                    toggleMenu={props.toggleMenu}
                    refresh={fetchData}
                />
            </div>

            <div className="row row-cols-1">
                <div className="col">
                    <ParameterList
                        data={displayData}
                        loadingState={loading}
                        onCellEdit={handleCellEdit}
                        onNewParameter={handleNewParameter}
                        onDeleteParameter={handleDeleteParameter}
                        getUniqueParameterValues={getUniqueParameterValues}
                        handleSaveChanges={handleSaveChanges}
                        isSaving={loading}
                        hasUnsavedChanges={hasUnsavedChanges}
                        isAdmin={props.isAdmin}
                    />
                </div>
            </div>
        </div>
    );
}

export default Parameters;
